import {
  Checkbox,
  Dialog,
  IconButton,
  Link,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { CheckedIconCircle, CloseCircle, RadioUnCheckIcon } from 'assets';
import ArrowRightIcon from 'assets/icons/AllowRightIcon';
import { NotifyService } from 'config';
import html2canvas from 'html2canvas-pro';
import { jsPDF } from 'jspdf';
import { ENUM_TYPE_OF_TERMS } from 'models/create-request';
import { useState } from 'react';
import { APP_ROUTES } from 'routers/routes';
import { uploadAPIs } from 'services/upload';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setGlobalLoading } from 'store/reducers/global';
import {
  updateStepTrial,
  updateUrlServiceAgreementPDF,
} from 'store/reducers/trial-request';
import { theme } from 'theme';

import {
  FullNonTerm,
  FullPackageTerm,
  FullSearchFeeTerm,
  FullTrialTerm,
} from '../FullTerms';
import ParentSignature from '../ParentSignature';
import { ConvertPDFtoHTML } from '../PDFs';
import {
  DialogSubTitleText,
  DialogTitleText,
  StyledDialogContent,
  StyledDialogTitle,
  TermContentText,
  TermContentTextBold,
  TermDescriptionWrapper,
  TermHeadItemWrapper,
  TermViewAllWrapper,
} from './styles';

interface TermsDialogProps {
  onClose: () => void;
}

const TermsDialog = ({ onClose, ...rest }: TermsDialogProps) => {
  const dispatch = useAppDispatch();
  const { parent } = useAppSelector((state) => state.auth);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isCheckSearchFee, setIsCheckSearchFee] = useState<boolean>(false);
  const [isCheckTrial, setIsCheckTrial] = useState<boolean>(false);
  const [isCheckPackages, setIsCheckPackages] = useState<boolean>(false);
  const [isCheckSolicitation, setIsCheckSolicitation] =
    useState<boolean>(false);
  const [isTermsOpen, setIsTermsOpen] = useState<string>('');
  // const [isScrolledToBottom, setIsBottom] = useState<boolean>(false);

  const _onClose = () => {
    setIsTermsOpen('');
    onClose();
  };

  const onSubmitCallback = () => {
    convertJsxToPDF();
  };

  const convertJsxToPDF = async () => {
    dispatch(setGlobalLoading(true));
    const allPage = document.querySelectorAll('#terms-pdf .page');

    const pdf = new jsPDF('p', 'mm', 'a4');
    for (let i = 0; i < allPage.length; i++) {
      const page = allPage[i] as HTMLDivElement;
      const canvas = await html2canvas(page as HTMLElement, {
        allowTaint: true,
        useCORS: true,
        scale: 0.9,
      });
      const imgData = canvas.toDataURL('image/jpeg', 1.0);

      if (i > 0) {
        pdf.addPage();
      }

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgProps = pdf.getImageProperties(imgData);
      const aspectRatio = imgProps.width / imgProps.height;
      let imgFinalWidth = pdfWidth;
      let imgFinalHeight = pdfWidth / aspectRatio;

      if (imgFinalHeight > pdfHeight) {
        imgFinalHeight = pdfHeight;
        imgFinalWidth = pdfHeight * aspectRatio;
      }

      const xOffset = (pdfWidth - imgFinalWidth) / 2;
      const yOffset = (pdfHeight - imgFinalHeight) / 2;

      pdf.addImage(
        imgData,
        'JPEG',
        xOffset,
        yOffset,
        imgFinalWidth,
        imgFinalHeight,
        undefined,
        'MEDIUM',
      );
    }

    // Create PDF file
    const pdfBlob = pdf.output('blob');
    const pdfFileName = `Service_Agreement.pdf`;
    const pdfFile = new File([pdfBlob], pdfFileName, {
      type: 'application/pdf',
    });

    // Upload to S3
    uploadPDFtoS3(pdf, pdfFile);
  };

  const uploadPDFtoS3 = async (_pdf: jsPDF, file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    await uploadAPIs
      .uploadFile(formData)
      .then(({ data }) => {
        const urlPDF = data?.data.url;
        // pdf.save('Service_Agreement.pdf');
        dispatch(updateUrlServiceAgreementPDF(urlPDF));
        savePDFtoDB(urlPDF);
      })
      .catch((e) => {
        NotifyService.error(e);
      })
      .finally(() => {
        dispatch(setGlobalLoading(false));
      });
  };

  const savePDFtoDB = async (s3Url: string) => {
    await uploadAPIs
      .createServiceAgreement({
        parent_id: parent.parent_id,
        s3_url: s3Url ?? '',
      })
      .then(() => {
        dispatch(updateStepTrial(3));
        _onClose();
      })
      .catch((e) => {
        NotifyService.error(e);
      })
      .finally(() => {
        dispatch(setGlobalLoading(false));
      });
  };

  // const handleScroll = useCallback(
  //   (event: React.UIEvent<HTMLDivElement>) => {
  //     const element = event.currentTarget;
  //     const hasScrolledToEnd =
  //       Math.abs(
  //         element.scrollHeight - element.scrollTop - element.clientHeight,
  //       ) <= 1;
  //     if (hasScrolledToEnd && !isScrolledToBottom) {
  //       setIsBottom(true);
  //     }
  //   },
  //   [isScrolledToBottom],
  // );

  const displayTitleFullTerms = () => {
    switch (isTermsOpen) {
      case ENUM_TYPE_OF_TERMS.SEARCH_FEE:
        return 'Search Fee';
      case ENUM_TYPE_OF_TERMS.TRIAL:
        return 'Trial';
      case ENUM_TYPE_OF_TERMS.PACKAGES:
        return 'Packages';
      case ENUM_TYPE_OF_TERMS.NON_SOLICITATION:
        return 'Non-Solicitation';
      default:
        break;
    }
  };
  const displayFullTerms = () => {
    switch (isTermsOpen) {
      case ENUM_TYPE_OF_TERMS.SEARCH_FEE:
        return <FullSearchFeeTerm />;
      case ENUM_TYPE_OF_TERMS.TRIAL:
        return <FullTrialTerm />;
      case ENUM_TYPE_OF_TERMS.PACKAGES:
        return <FullPackageTerm />;
      case ENUM_TYPE_OF_TERMS.NON_SOLICITATION:
        return <FullNonTerm />;
      default:
        break;
    }
  };

  const viewFullTerms = (typeOfTerms: string) => {
    setIsTermsOpen(typeOfTerms);
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    typeOfTerms: string,
  ) => {
    const isCheck = event.target.checked;
    switch (typeOfTerms) {
      case ENUM_TYPE_OF_TERMS.SEARCH_FEE:
        setIsCheckSearchFee(isCheck);
        break;
      case ENUM_TYPE_OF_TERMS.TRIAL:
        setIsCheckTrial(isCheck);
        break;
      case ENUM_TYPE_OF_TERMS.PACKAGES:
        setIsCheckPackages(isCheck);
        break;
      case ENUM_TYPE_OF_TERMS.NON_SOLICITATION:
        setIsCheckSolicitation(isCheck);
        break;
      default:
        break;
    }
  };

  const TermOfSearchFee = () => (
    <Stack spacing={0.5}>
      <TermHeadItemWrapper>
        <Stack direction={'row'} alignItems={'center'}>
          <Checkbox
            checked={isCheckSearchFee}
            onChange={(e) => handleChange(e, ENUM_TYPE_OF_TERMS.SEARCH_FEE)}
            icon={<RadioUnCheckIcon />}
            checkedIcon={<CheckedIconCircle sx={{ color: '#34C759' }} />}
            sx={{ width: '25px', height: '29px' }}
          />
          &nbsp;
          <Typography className="title-item">Search Fee</Typography>
          &nbsp;
          <Typography className="required-item">[Required]</Typography>
        </Stack>
        <TermViewAllWrapper
          onClick={() => viewFullTerms(ENUM_TYPE_OF_TERMS.SEARCH_FEE)}
        >
          <Typography>Read All</Typography>
          <ArrowRightIcon />
        </TermViewAllWrapper>
      </TermHeadItemWrapper>
      <TermDescriptionWrapper>
        <TermContentText>
          To kickstart the sitter search, a search fee of&nbsp;
          <TermContentTextBold>S$50</TermContentTextBold> is required. If a
          match is not found, the search fee will be refunded to you.
        </TermContentText>
      </TermDescriptionWrapper>
    </Stack>
  );

  const TermOfTrialJob = () => (
    <Stack spacing={0.5}>
      <TermHeadItemWrapper>
        <Stack direction={'row'} alignItems={'center'}>
          <Checkbox
            checked={isCheckTrial}
            onChange={(e) => handleChange(e, ENUM_TYPE_OF_TERMS.TRIAL)}
            icon={<RadioUnCheckIcon />}
            checkedIcon={<CheckedIconCircle sx={{ color: '#34C759' }} />}
            sx={{ width: '25px', height: '29px' }}
          />
          &nbsp;
          <Typography className="title-item">Trial</Typography>
          &nbsp;
          <Typography className="required-item">[Required]</Typography>
        </Stack>
        <TermViewAllWrapper
          onClick={() => viewFullTerms(ENUM_TYPE_OF_TERMS.TRIAL)}
        >
          <Typography>Read All</Typography>
          <ArrowRightIcon />
        </TermViewAllWrapper>
      </TermHeadItemWrapper>
      <TermDescriptionWrapper>
        <TermContentText>
          Once you've chosen your sitter profile, we'll start arranging a trial
          session that lasts a minimum of&nbsp;
          <TermContentTextBold>3 hours</TermContentTextBold>. After the trial,
          you can decide if you want to continue the service with the same
          sitter or request for a second trial with a different sitter.
        </TermContentText>
      </TermDescriptionWrapper>
    </Stack>
  );

  const TermOfPackages = () => (
    <Stack spacing={0.5}>
      <TermHeadItemWrapper>
        <Stack direction={'row'} alignItems={'center'}>
          <Checkbox
            checked={isCheckPackages}
            onChange={(e) => handleChange(e, ENUM_TYPE_OF_TERMS.PACKAGES)}
            icon={<RadioUnCheckIcon />}
            checkedIcon={<CheckedIconCircle sx={{ color: '#34C759' }} />}
            sx={{ width: '25px', height: '29px' }}
          />
          &nbsp;
          <Typography className="title-item">Packages</Typography>
          &nbsp;
          <Typography className="required-item">[Required]</Typography>
        </Stack>
        <TermViewAllWrapper
          onClick={() => viewFullTerms(ENUM_TYPE_OF_TERMS.PACKAGES)}
        >
          <Typography>Read All</Typography>
          <ArrowRightIcon />
        </TermViewAllWrapper>
      </TermHeadItemWrapper>
      <TermDescriptionWrapper>
        <TermContentText>
          Trial rates are fixed at $35/hr and Aunty Concierge plans and rates
          are as follows. There's a surcharge of $5/hr for urgent requests made
          within 48 hours, a surcharge of $5/hr for every additional child and a
          penalty of 1 hour of the hourly rate for cancellations made 24 hours
          before the start of the job.
        </TermContentText>
      </TermDescriptionWrapper>
    </Stack>
  );

  const TermOfNonSolicitation = () => (
    <Stack spacing={0.5}>
      <TermHeadItemWrapper>
        <Stack direction={'row'} alignItems={'center'}>
          <Checkbox
            checked={isCheckSolicitation}
            onChange={(e) =>
              handleChange(e, ENUM_TYPE_OF_TERMS.NON_SOLICITATION)
            }
            icon={<RadioUnCheckIcon />}
            checkedIcon={<CheckedIconCircle sx={{ color: '#34C759' }} />}
            sx={{ width: '25px', height: '28px' }}
          />
          &nbsp;
          <Typography className="title-item">Non-Solicitation</Typography>
          &nbsp;
          <Typography className="required-item">[Required]</Typography>
        </Stack>

        <TermViewAllWrapper
          onClick={() => viewFullTerms(ENUM_TYPE_OF_TERMS.NON_SOLICITATION)}
        >
          <Typography>Read All</Typography>
          <ArrowRightIcon />
        </TermViewAllWrapper>
      </TermHeadItemWrapper>
      <TermDescriptionWrapper>
        <TermContentText>
          You are not allowed to contact our babysitters directly, outside of
          Aunty’s services. If you wish to continue with or change your sitter,
          please notify Aunty. Any exchange of personal contact information will
          result in the termination of your profile on our platform.
        </TermContentText>
      </TermDescriptionWrapper>
    </Stack>
  );

  return (
    <Dialog
      open
      onClose={_onClose}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          maxWidth: '650px',
          m: { xs: 3, md: 5 },
          p: { xs: 2, md: 5 },
          gap: 3,
        },
      }}
      {...rest}
    >
      <div style={{ height: 0.1, overflow: 'hidden' }}>
        <ConvertPDFtoHTML />
      </div>
      <StyledDialogTitle>
        {isTermsOpen === '' ? (
          <>
            <DialogTitleText>Aunty Concierge Service Agreement</DialogTitleText>
            <DialogSubTitleText>
              Kindly read and accept before the following terms of trial before
              proceeding.
            </DialogSubTitleText>
          </>
        ) : (
          <>
            <DialogTitleText mb={1} justifyContent={'space-between'}>
              <Typography>{displayTitleFullTerms()}</Typography>
              <IconButton size="small" onClick={() => setIsTermsOpen('')}>
                <CloseCircle sx={{ color: theme.palette.text.secondary }} />
              </IconButton>
            </DialogTitleText>
          </>
        )}
      </StyledDialogTitle>
      <StyledDialogContent>
        <Stack spacing={2}>
          {isTermsOpen === '' ? (
            <>
              <TermOfSearchFee />
              <TermOfTrialJob />
              <TermOfPackages />
              <TermOfNonSolicitation />
            </>
          ) : (
            displayFullTerms()
          )}
        </Stack>
      </StyledDialogContent>
      <Stack spacing={1}>
        {isTermsOpen === '' && (
          <Link
            href={APP_ROUTES.SERVICE_ASSESSMENT.to}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            fontSize={12}
            lineHeight={'20px'}
            color={'secondary.main'}
          >
            Click here to view full Terms of Service
          </Link>
        )}
        {isTermsOpen === '' && (
          <Stack spacing={1}>
            <Typography variant="Web_Label_14">
              Your Signature (Please type your First Name below)
              <span style={{ color: theme.palette.red.light }}>*</span>
            </Typography>
            <ParentSignature
              onSubmitCallback={onSubmitCallback}
              isTickAll={
                isCheckPackages &&
                isCheckSearchFee &&
                isCheckTrial &&
                isCheckSolicitation
              }
            />
          </Stack>
        )}
      </Stack>
    </Dialog>
  );
};

export default TermsDialog;
